import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  finalize,
  first,
  forkJoin,
  of,
  tap,
} from 'rxjs';
import { GoodsCategoryOption } from 'src/app/core/models/goods-category/goods-category-option.interface';
import { GoodsCategory } from 'src/app/core/models/goods-category/goods-category.interface';
import { InventoryItemStatuses } from 'src/app/core/enums/inventory-item-statuses.enum';
import {
  INVENTORY_ITEM_TYPES,
  InventoryItemType,
  getInventoryItemTypeLabelKey,
  isInventoryItemType,
} from 'src/app/core/enums/inventory-item-type.enum';
import { InventoryItemGeneralInfoUpdate } from 'src/app/core/models/inventory/inventory-item-general-info-update.interface';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { InventoryItemOverviewFormModel } from 'src/app/core/models/inventory/inventory-item-overview-form.model';
import {
  InventoryItemOverviewEditableFieldName,
  InventoryItemOverviewField,
} from 'src/app/core/models/inventory/inventory-item-overview-view-model.interface';
import { CoreModule } from 'src/app/core/core.module';
import { GoodsCategoriesService } from 'src/app/core/services/goods-categories.service';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { isConcurrencyConflictError } from 'src/app/core/utils/api-error.util';

type InventoryItemInfoEditableFieldName = Extract<
  InventoryItemOverviewEditableFieldName,
  'displayName' | 'displayName2' | 'goodsCategoryId' | 'itemType' | 'status' | 'manufacturer' | 'model'
>;

interface InventoryItemInfoViewModel {
  infoFields: Array<InventoryItemOverviewField & { controlName: InventoryItemInfoEditableFieldName }>;
  previewFieldLabelKey?: string;
}

type InventoryItemPreviewUpdater = (
  item: InventoryItem,
  value: string | InventoryItemStatuses | null,
) => Omit<InventoryItemGeneralInfoUpdate, 'expectedConcurrencyToken'>;

const ITEM_TYPE_LABEL_KEY_MAP: Readonly<Record<InventoryItemType, string>> = {
  Product: 'COMPONENTS.GOODS.ITEM_TYPES.PRODUCT',
  Service: 'COMPONENTS.GOODS.ITEM_TYPES.SERVICE',
  Hybrid: 'COMPONENTS.GOODS.ITEM_TYPES.HYBRID',
};

@Component({
  selector: 'app-inventory-item-info-tab',
  templateUrl: './inventory-item-info-tab.component.html',
  styleUrl: './inventory-item-info-tab.component.scss',
  imports: [CommonModule, ReactiveFormsModule, CoreModule],
  providers: [InventoryItemOverviewFormModel],
  standalone: true,
})
export class InventoryItemInfoTabComponent implements OnChanges {
  readonly overviewForm = inject(InventoryItemOverviewFormModel);
  private readonly inventoryItemService = inject(InventoryItemService);
  private readonly goodsCategoriesService = inject(GoodsCategoriesService);

  public readonly inventoryItemStatuses = InventoryItemStatuses;
  public readonly itemTypeOptions: { key: string; value: InventoryItemType }[] = INVENTORY_ITEM_TYPES.map(
    (value) => ({ key: ITEM_TYPE_LABEL_KEY_MAP[value], value }),
  );
  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public itemId = '';
  @Input() public inventoryItem: InventoryItem | null = null;
  @Output() public readonly inventoryItemChange = new EventEmitter<InventoryItem>();
  @Output() public readonly concurrencyConflict = new EventEmitter<void>();

  public isLoading = false;
  public isCompositeSaving = false;
  public isCompositeChecked = false;
  // i18n key surfaced inline next to the composite toggle on 409 responses. The backend
  // returns 409 either because the item has children that block toggling off, or because
  // the item is used as a child of another composite (toggling on is rejected). We map
  // both to dedicated keys so the user gets actionable feedback.
  public compositeErrorKey = '';
  public readonly inventoryItem$ = new BehaviorSubject<InventoryItem | null>(null);
  public readonly viewModel$ = new BehaviorSubject<InventoryItemInfoViewModel | null>(null);
  private categoryOptions: GoodsCategoryOption[] = [];

  private readonly patchUpdaters: Partial<Record<InventoryItemInfoEditableFieldName, InventoryItemPreviewUpdater>> = {
    displayName: (_, value) => ({ displayName: this.toOptionalString(value) }),
    displayName2: (_, value) => ({ displayName2: this.toOptionalString(value) }),
    goodsCategoryId: (_, value) => ({ goodsCategoryId: this.toOptionalGuid(value) }),
    itemType: (_, value) => ({ itemType: this.toItemType(value) }),
    status: (_, value) => ({ status: value as InventoryItemStatuses }),
    manufacturer: (_, value) => ({ manufacturer: this.toOptionalString(value) }),
    model: (_, value) => ({ model: this.toOptionalString(value) }),
  };

  @Input() public reloadToken = '';

  public onCompositeToggleChange(value: boolean): void {
    const currentItem = this.inventoryItem$.getValue();
    if (!currentItem || this.isCompositeSaving) {
      this.isCompositeChecked = currentItem?.isComposite === true;
      return;
    }

    this.isCompositeSaving = true;
    this.compositeErrorKey = '';

    this.inventoryItemService
      .setIsComposite(this.companyId, this.itemId, value, currentItem.concurrencyToken ?? '')
      .pipe(
        first(),
        tap((inventoryItem) => {
          this.syncInventoryItemState(inventoryItem);
          this.inventoryItemChange.emit(inventoryItem);
        }),
        catchError((error) => this.handleCompositeError(error, currentItem, value)),
        finalize(() => {
          this.isCompositeSaving = false;
        }),
      )
      .subscribe();
  }

  private handleCompositeError(error: unknown, currentItem: InventoryItem, attemptedValue: boolean) {
    // Restore the checkbox to the persisted state so the UI does not lie about
    // the backend value while we surface the rejection reason.
    this.isCompositeChecked = currentItem.isComposite === true;
    if (error instanceof HttpErrorResponse && error.status === 409) {
      this.compositeErrorKey = attemptedValue
        ? 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.COMPOSITE_BLOCKED_BY_PARENT_USAGE'
        : 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.COMPOSITE_BLOCKED_BY_CHILDREN';
      if (isConcurrencyConflictError(error)) {
        this.concurrencyConflict.emit();
      }
    } else {
      this.compositeErrorKey = 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.COMPOSITE_SAVE_FAILED';
    }
    return EMPTY;
  }

  public onPreviewFieldUpdate(fieldName: InventoryItemInfoEditableFieldName): void {
    const currentItem = this.inventoryItem$.getValue();
    if (!currentItem) {
      return;
    }

    const updater = this.patchUpdaters[fieldName];
    if (!updater) {
      this.syncInventoryItemState(currentItem);
      return;
    }

    const rawValue = this.overviewForm.form.controls[fieldName].value ?? '';
    const trimmedValue = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    const changes = this.withConcurrencyMetadata(currentItem, updater(currentItem, trimmedValue));

    this.inventoryItemService
      .updateInventoryItemGeneralInfo(this.companyId, this.itemId, changes)
      .pipe(
        first(),
        tap((inventoryItem) => {
          this.syncInventoryItemState(inventoryItem, this.getFieldLabelKey(fieldName));
          this.inventoryItemChange.emit(inventoryItem);
        }),
        catchError((error) => {
          this.syncInventoryItemState(currentItem);
          if (isConcurrencyConflictError(error)) {
            this.concurrencyConflict.emit();
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['companyId'] && !changes['itemId'] && !changes['reloadToken'] && !changes['inventoryItem']) {
      return;
    }

    if (this.inventoryItem) {
      this.loadCategoryOptions();
      this.syncInventoryItemState(this.inventoryItem);
      return;
    }

    this.loadInventoryItem();
  }

  private loadInventoryItem(): void {
    if (!this.companyId || !this.itemId) {
      return;
    }

    this.isLoading = true;
    forkJoin({
      inventoryItem: this.inventoryItemService.getInventoryItem(this.companyId, this.itemId),
      goodsCategories: this.goodsCategoriesService.getCompanyGoodsCategories(this.companyId).pipe(catchError(() => of([]))),
    })
      .pipe(
        first(),
        tap(({ inventoryItem, goodsCategories }) => {
          this.categoryOptions = this.flattenGoodsCategories(goodsCategories);
          this.syncInventoryItemState(inventoryItem);
        }),
        catchError(() => EMPTY),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private loadCategoryOptions(): void {
    if (!this.companyId) {
      return;
    }

    this.goodsCategoriesService
      .getCompanyGoodsCategories(this.companyId)
      .pipe(
        first(),
        tap((goodsCategories) => {
          this.categoryOptions = this.flattenGoodsCategories(goodsCategories);
          const currentItem = this.inventoryItem$.getValue() ?? this.inventoryItem;
          if (currentItem) {
            this.viewModel$.next(this.buildOverviewViewModel(currentItem, this.viewModel$.getValue()?.previewFieldLabelKey));
          }
        }),
        catchError(() => {
          this.categoryOptions = [];
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private syncInventoryItemState(
    inventoryItem: InventoryItem,
    previewFieldLabelKey?: string,
  ): void {
    this.inventoryItem$.next(inventoryItem);
    this.isCompositeChecked = inventoryItem.isComposite === true;
    this.compositeErrorKey = '';
    this.overviewForm.form.patchValue(
      {
        displayName: inventoryItem.displayName ?? '',
        displayName2: inventoryItem.displayName2 ?? '',
        goodsCategoryId: inventoryItem.goodsCategoryId ?? null,
        itemType: inventoryItem.itemType ?? 'Product',
        status: inventoryItem.status ?? InventoryItemStatuses.Active,
        manufacturer: inventoryItem.manufacturer ?? '',
        model: inventoryItem.model ?? '',
        unitPrice: inventoryItem.unitPrice?.toString() ?? '',
        unitCost: inventoryItem.unitCost?.toString() ?? '',
      },
      { emitEvent: false },
    );
    this.viewModel$.next(this.buildOverviewViewModel(inventoryItem, previewFieldLabelKey));
  }

  private buildOverviewViewModel(
    item: InventoryItem,
    previewFieldLabelKey?: string,
  ): InventoryItemInfoViewModel {
    const isEditable = item.isArchived !== true;

    return {
      previewFieldLabelKey,
      infoFields: [
        {
          controlName: 'displayName',
          label: 'COMPONENTS.GOODS.FIELDS.NAME',
          value: item.displayName ?? '',
          editable: isEditable,
        },
        {
          controlName: 'displayName2',
          label: 'COMPONENTS.GOODS.FIELDS.ADDITIONAL_NAME',
          value: item.displayName2 ?? '',
          editable: isEditable,
        },
        {
          controlName: 'goodsCategoryId',
          label: 'COMPONENTS.GOODS.FIELDS.CATEGORY',
          value: this.resolveCategoryLabel(item),
          inputType: 'select',
          editable: isEditable,
          selectItems: [
            { key: 'COMPONENTS.GOODS.CATEGORY_PLACEHOLDER', value: null },
            ...this.categoryOptions.map((category) => ({ key: category.label, value: category.id })),
          ],
        },
        {
          controlName: 'itemType',
          label: 'COMPONENTS.GOODS.FIELDS.TYPE',
          value: item.itemType ?? '',
          valueKey: this.resolveItemTypeValueKey(item.itemType),
          inputType: 'select',
          editable: isEditable,
          selectItems: this.itemTypeOptions as { key: string; value: unknown }[],
        },
        {
          controlName: 'manufacturer',
          label: 'COMPONENTS.GOODS.FIELDS.MANUFACTURER',
          value: item.manufacturer ?? '',
          editable: isEditable,
        },
        {
          controlName: 'model',
          label: 'COMPONENTS.GOODS.FIELDS.MODEL',
          value: item.model ?? '',
          editable: isEditable,
        },
        {
          controlName: 'status',
          label: 'COMPONENTS.GOODS.FIELDS.STATUS',
          value: InventoryItemStatuses[item.status ?? InventoryItemStatuses.Active],
          valueKey: `INVENTORY_ITEM_STATUSES.${InventoryItemStatuses[item.status ?? InventoryItemStatuses.Active].toUpperCase()}`,
          inputType: 'select',
          enumName: 'INVENTORY_ITEM_STATUSES',
          enumObject: InventoryItemStatuses,
          editable: isEditable,
          selectItems: [
            { key: 'INVENTORY_ITEM_STATUSES.ACTIVE', value: InventoryItemStatuses.Active },
            { key: 'INVENTORY_ITEM_STATUSES.BLOCKED', value: InventoryItemStatuses.Blocked },
          ],
        },
      ],
    };
  }

  private getFieldLabelKey(fieldName: InventoryItemInfoEditableFieldName): string {
    const labels: Record<InventoryItemInfoEditableFieldName, string> = {
      displayName: 'COMPONENTS.GOODS.FIELDS.NAME',
      displayName2: 'COMPONENTS.GOODS.FIELDS.ADDITIONAL_NAME',
      goodsCategoryId: 'COMPONENTS.GOODS.FIELDS.CATEGORY',
      itemType: 'COMPONENTS.GOODS.FIELDS.TYPE',
      status: 'COMPONENTS.GOODS.FIELDS.STATUS',
      manufacturer: 'COMPONENTS.GOODS.FIELDS.MANUFACTURER',
      model: 'COMPONENTS.GOODS.FIELDS.MODEL',
    };

    return labels[fieldName];
  }

  private toOptionalString(value: string | InventoryItemStatuses | null): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    return value ? value : undefined;
  }

  private toOptionalGuid(value: string | InventoryItemStatuses | null): string | null | undefined {
    if (typeof value !== 'string') {
      return value === null ? null : undefined;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : null;
  }

  private resolveCategoryLabel(item: InventoryItem): string {
    if (item.goodsCategoryId) {
      const matchedCategory = this.categoryOptions.find((category) => category.id === item.goodsCategoryId);
      if (matchedCategory) {
        return matchedCategory.label;
      }
    }

    return item.categoryTitle ?? '';
  }

  private resolveItemTypeValueKey(itemType?: InventoryItemType | string | null): string | undefined {
    if (!itemType) {
      return undefined;
    }

    if (isInventoryItemType(itemType)) {
      return getInventoryItemTypeLabelKey(itemType);
    }

    return undefined;
  }

  private toItemType(value: string | InventoryItemStatuses | null): InventoryItemType | undefined {
    return isInventoryItemType(value) ? value : undefined;
  }

  private flattenGoodsCategories(goodsCategories: GoodsCategory[], depth = 0): GoodsCategoryOption[] {
    return goodsCategories.flatMap((category) => {
      const currentItem: GoodsCategoryOption = {
        id: category.id,
        title: category.title,
        depth,
        label: `${depth ? '— '.repeat(depth) : ''}${category.title}`,
      };

      return [currentItem, ...this.flattenGoodsCategories(category.children, depth + 1)];
    });
  }

  private withConcurrencyMetadata<T extends object>(
    inventoryItem: InventoryItem,
    changes: T,
  ): T & { expectedConcurrencyToken: string } {
    return {
      ...changes,
      expectedConcurrencyToken: inventoryItem.concurrencyToken ?? '',
    };
  }
}
